/**
 * This module provides functions in relation to calling API routes on the backend server and processing the incoming
 * responses. The functions in this module refer to the API endpoint ApiEndPoints.PORTFOLIO.
 * @module
 */

import {ApiEndPoints, HttpMethods} from "../base/Settings";
import type {FetchRequest, PortfolioInput, Portfolio, UUID} from "../base/ui_general/GeneralInterfaces";


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the creation of a new
 * portfolio on the server. Only name, description and portfolio type need to be specified for this purpose.
 * @param portfolioInput An object of type PortfolioInput containing the input data for creating the portfolio.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_create_portfolio(portfolioInput: PortfolioInput): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.PORTFOLIO,
        method: HttpMethods.POST,
        query_parameters: {},
        body: portfolioInput,
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for updating an existing
 * portfolio on the server.
 * @param portfolio An object of type Portfolio containing the updated portfolio data.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_update_portfolio(portfolio: Portfolio): FetchRequest {
    const portfolioPayload = Object.fromEntries(
        Object.entries(portfolio).filter(([key]) => key !== "portfolio_rows")
    );
    return {
        api_endpoint: ApiEndPoints.PORTFOLIO,
        method: HttpMethods.PUT,
        query_parameters: {},
        body: portfolioPayload,
    };
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the query of portfolios
 * on the server.
 * @param transaction_id .The UUID of the transaction to query for.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_get_all_portfolios(transaction_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.PORTFOLIO,
        method: HttpMethods.GET,
        query_parameters: { transaction_id: transaction_id },
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for deleting an existing
 * portfolio on the server.
 * @param portfolio_id The UUID of the portfolio to delete.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_delete_portfolio(portfolio_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.PORTFOLIO,
        method: HttpMethods.DELETE,
        query_parameters: { portfolio_id: portfolio_id },
    };
}
